<template>
	<div class="infoboard-chart">
		<div
			class="infoboard-chart__chart"
			ref="chart"
		/>
	</div>
</template>

<script lang='ts'>
import { defineComponent } from 'vue'
import { Rotation } from '@libs/interfaces/interfaces'
import * as Highcharts from 'highcharts'
import createChart from '@/assets/libs/models/rotations'

export default defineComponent({
	name: 'InfoboardChart',
	props: {
		rotation: {
			type: Object as () => Rotation,
			required: true
		},
		locale: {
			type: String,
			default: 'en-GB'
		}
	},
	data() {
		return {
			chart: undefined as Highcharts.Chart | undefined,
			resizeHandler: undefined as (() => void) | undefined
		}
	},
	computed: {
		computedChart(): Highcharts.Options {
			return createChart(this.rotation, this.locale)
		}
	},
	methods: {
		renderChart(chartData: Highcharts.Options) {
			this.$nextTick(() => { // ref is possibly undefined unless deferred
				const ref = this.$refs.chart as HTMLElement
				if (!(chartData && ref)) return

				ref.classList.remove('animation--fade')
				const options = Highcharts.merge(chartData, {
					chart: {
						renderTo: ref as HTMLElement
					}
				})

				this.chart?.destroy?.()
				this.chart = Highcharts.chart(options)
				this.chart.reflow()
				ref.classList.add('animation--fade')
			})
		}
	},
	watch: {
		computedChart: {
			handler(chartData: Highcharts.Options) {
				this.renderChart(chartData)
			},
			immediate: true,
			deep: true
		}
	},
	mounted() {
		this.resizeHandler = () => {
			this.renderChart(createChart(this.rotation, this.locale))
		}
		window.addEventListener('resize', this.resizeHandler)
	},
	beforeUnmount() {
		if (this.resizeHandler) {
			window.removeEventListener('resize', this.resizeHandler)
		}
		this.chart?.destroy?.()
	}
})
</script>

<style lang="scss">
.infoboard-chart {
	width: 100%;
	font-weight: 500;

	&__chart {
		transform: translateY(25px);
		overflow: visible !important;
		> * {
			margin: 0 auto;
			width: 100%;
			overflow: visible !important;
		}
		svg {
			overflow: visible;
		}
	}
}
</style>
